import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Smartphone, Monitor, Apple, Share, Plus, MoreVertical, Download, CheckCircle2, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

type DeviceType = "ios" | "ios-non-safari" | "android" | "desktop" | "unknown";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// Detect if user is on iOS but NOT using Safari
const isIOSNonSafari = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isNotSafari = /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIOS && isNotSafari;
};

const Instalar = () => {
  const { t } = useTranslation();
  const [deviceType, setDeviceType] = useState<DeviceType>("unknown");
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    if (isIOSNonSafari()) {
      setDeviceType("ios-non-safari");
    } else if (/iphone|ipad|ipod/.test(userAgent)) {
      setDeviceType("ios");
    } else if (/android/.test(userAgent)) {
      setDeviceType("android");
    } else {
      setDeviceType("desktop");
    }

    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      toast.success("Link copiado! Agora abra o Safari e cole na barra de endereço.");
    } catch {
      toast.error("Não foi possível copiar o link");
    }
  };

  const benefits = [
    { icon: "⚡", title: "Acesso Rápido", desc: "Abra o app direto da tela inicial" },
    { icon: "📱", title: "Tela Cheia", desc: "Experiência sem barras do navegador" },
    { icon: "📶", title: "Offline", desc: "Acesse conteúdo mesmo sem internet" },
    { icon: "🔄", title: "Atualizações", desc: "Sempre a versão mais recente" },
  ];

  return (
    <Layout>
      <SEO
        title="Instalar App | TUPJA"
        description="Instale o app do TUPJA no seu dispositivo para acesso rápido ao terreiro, agenda de giras e conteúdo espiritual."
        keywords="TUPJA app, instalar PWA, terreiro umbanda app, giras Orlando"
      />

      <main className="min-h-screen bg-background">
        {/* Header */}
        <section className="bg-logo-brown-dark text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <div className="flex justify-center mb-6">
              <img
                src="/logo-tupja.png"
                alt="TUPJA Logo"
                className="w-24 h-24 rounded-2xl shadow-lg"
              />
            </div>
            <h1 className="font-display text-3xl md:text-4xl mb-4">Instale o App TUPJA</h1>
            <p className="font-sans text-white/80 max-w-md mx-auto">
              Tenha o terreiro sempre à mão. Acesse a agenda, trabalhos espirituais e muito mais direto do seu celular.
            </p>
          </div>
        </section>

        <div className="container mx-auto px-4 py-12">
          {/* Already Installed */}
          {isInstalled && (
            <Card className="mb-8 border-green-500 bg-green-50 dark:bg-green-950">
              <CardContent className="flex items-center gap-4 py-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
                <div>
                  <p className="font-display text-lg text-green-700 dark:text-green-300">
                    App já instalado!
                  </p>
                  <p className="text-green-600 dark:text-green-400 text-sm">
                    Você já está usando o TUPJA como aplicativo.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Benefits */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center">
                <CardContent className="pt-6">
                  <span className="text-3xl mb-2 block">{benefit.icon}</span>
                  <h3 className="font-display text-sm mb-1">{benefit.title}</h3>
                  <p className="text-xs text-muted-foreground">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Installation Instructions */}
          <div className="space-y-8">
            {/* Warning for iOS users not on Safari */}
            {deviceType === "ios-non-safari" && (
              <Card className="border-2 border-orange-500 bg-orange-50 dark:bg-orange-950">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
                    <AlertTriangle className="w-5 h-5" />
                    Use o Safari para instalar
                  </CardTitle>
                  <CardDescription className="text-orange-600 dark:text-orange-400">
                    No iPhone/iPad, apenas o Safari permite instalar apps na tela inicial.
                    Você está usando outro navegador.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-orange-700 dark:text-orange-300">
                    Copie o link abaixo e abra no Safari:
                  </p>
                  <div className="flex items-center gap-2 p-3 bg-orange-100 dark:bg-orange-900 rounded-lg">
                    <code className="flex-1 text-sm truncate">{window.location.origin}</code>
                    <Button size="sm" variant="secondary" onClick={handleCopyLink}>
                      <Copy className="w-4 h-4 mr-1" />
                      Copiar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Android with Install Button */}
            {deferredPrompt && (
              <Card className="border-2 border-primary">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="w-5 h-5" />
                    Instalação Rápida
                  </CardTitle>
                  <CardDescription>
                    Clique no botão abaixo para instalar o app
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleInstall} size="lg" className="w-full">
                    <Download className="w-5 h-5 mr-2" />
                    Instalar App TUPJA
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* iOS Instructions */}
            <Card className={deviceType === "ios" ? "border-2 border-primary" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Apple className="w-5 h-5" />
                  iPhone / iPad (Safari)
                </CardTitle>
                <CardDescription>
                  Siga os passos abaixo para instalar no iOS
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      1
                    </span>
                    <div>
                      <p className="font-medium">Abra no Safari</p>
                      <p className="text-sm text-muted-foreground">
                        O Safari é necessário para instalar apps no iOS
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      2
                    </span>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">Toque em</p>
                      <Share className="w-5 h-5 text-blue-500" />
                      <p className="font-medium">Compartilhar</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      3
                    </span>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">Toque em</p>
                      <Plus className="w-5 h-5" />
                      <p className="font-medium">"Adicionar à Tela Inicial"</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      4
                    </span>
                    <div>
                      <p className="font-medium">Confirme tocando em "Adicionar"</p>
                    </div>
                  </li>
                </ol>
              </CardContent>
            </Card>

            {/* Android Instructions */}
            <Card className={deviceType === "android" && !deferredPrompt ? "border-2 border-primary" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  Android (Chrome)
                </CardTitle>
                <CardDescription>
                  Siga os passos abaixo para instalar no Android
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      1
                    </span>
                    <div>
                      <p className="font-medium">Abra no Chrome</p>
                      <p className="text-sm text-muted-foreground">
                        O Chrome é o navegador recomendado para Android
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      2
                    </span>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">Toque em</p>
                      <MoreVertical className="w-5 h-5" />
                      <p className="font-medium">(menu)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      3
                    </span>
                    <div>
                      <p className="font-medium">Toque em "Instalar aplicativo"</p>
                      <p className="text-sm text-muted-foreground">
                        Ou "Adicionar à tela inicial"
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      4
                    </span>
                    <div>
                      <p className="font-medium">Confirme a instalação</p>
                    </div>
                  </li>
                </ol>
              </CardContent>
            </Card>

            {/* Desktop Instructions */}
            <Card className={deviceType === "desktop" ? "border-2 border-primary" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Monitor className="w-5 h-5" />
                  Computador (Chrome/Edge)
                </CardTitle>
                <CardDescription>
                  Siga os passos abaixo para instalar no computador
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      1
                    </span>
                    <div>
                      <p className="font-medium">Procure o ícone de instalação</p>
                      <p className="text-sm text-muted-foreground">
                        No Chrome/Edge, aparece na barra de endereço (direita)
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      2
                    </span>
                    <div>
                      <p className="font-medium">Clique em "Instalar"</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      3
                    </span>
                    <div>
                      <p className="font-medium">O app abrirá em uma nova janela</p>
                      <p className="text-sm text-muted-foreground">
                        Um atalho será criado na área de trabalho
                      </p>
                    </div>
                  </li>
                </ol>
              </CardContent>
            </Card>
          </div>

          {/* Help Section */}
          <Card className="mt-12 bg-muted/50">
            <CardContent className="py-6 text-center">
              <p className="text-muted-foreground">
                Precisa de ajuda? Entre em contato conosco pelo{" "}
                <a
                  href="https://wa.me/14077758479"
                  className="text-primary hover:underline font-medium"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </Layout>
  );
};

export default Instalar;
